import { Component, computed, inject, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideBuilding2, lucideSearch, lucideCheck } from '@ng-icons/lucide';
import {
  BrnAlertDialogImports,
  HlmAlertDialogImports,
  HlmAvatarImports,
  HlmButtonImports,
  HlmInputImports,
} from '@pagai-fe/ui';
import { LoggedUserService } from '../../services/logged-user.service';
import { Tenant } from '../../interfaces/tenant.interface';

/**
 * Dialog component for selecting a tenant.
 * Used by Labor Consultants to switch between companies.
 * Features search filtering and visual indication of current selection.
 */
@Component({
  selector: 'app-tenant-selector-dialog',
  standalone: true,
  imports: [
    NgIcon,
    HlmAlertDialogImports,
    BrnAlertDialogImports,
    HlmAvatarImports,
    HlmButtonImports,
    HlmInputImports,
  ],
  providers: [provideIcons({ lucideBuilding2, lucideSearch, lucideCheck })],
  templateUrl: './tenant-selector-dialog.component.html',
})
export class TenantSelectorDialogComponent {
  private readonly _loggedUserService = inject(LoggedUserService);

  /** Output: tenant selected event */
  readonly tenantSelected = output<Tenant>();

  /** Dialog state */
  private readonly _isOpen = signal(false);
  protected readonly isOpen = this._isOpen.asReadonly();

  /** All available tenants */
  protected readonly tenants = toSignal(this._loggedUserService.tenantsSubject$, {
    initialValue: [],
  });

  /** Currently selected tenant ID */
  protected readonly selectedTenantId = toSignal(
    this._loggedUserService.selectedTenantIdSubject$,
    { initialValue: null },
  );

  /** Search query signal */
  protected readonly searchQuery = signal('');

  /** Filtered tenants based on search query */
  protected readonly filteredTenants = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const allTenants = this.tenants();

    if (!query) {
      return allTenants;
    }

    return allTenants.filter(
      (tenant) =>
        tenant.name.toLowerCase().includes(query) ||
        tenant.businessName?.toLowerCase().includes(query) ||
        tenant.code?.toLowerCase().includes(query),
    );
  });

  /**
   * Open the dialog
   */
  public open(): void {
    this.searchQuery.set('');
    this._isOpen.set(true);
  }

  /**
   * Close the dialog
   */
  public close(): void {
    this._isOpen.set(false);
  }

  /**
   * Handle dialog state change
   * @param state - New dialog state
   */
  protected onStateChange(state: 'open' | 'closed'): void {
    if (state === 'closed') {
      this.close();
    }
  }

  /**
   * Handle search input change
   * @param event - Input event
   */
  protected onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
  }

  /**
   * Select a tenant and close the dialog
   * @param tenant - The selected tenant
   */
  protected selectTenant(tenant: Tenant): void {
    this._loggedUserService.selectTenant(tenant.name);
    this.tenantSelected.emit(tenant);
    this.close();
  }

  /**
   * Check if a tenant is currently selected
   * @param tenant - Tenant to check
   * @returns True if this is the selected tenant
   */
  protected isSelected(tenant: Tenant): boolean {
    return tenant.name === this.selectedTenantId();
  }

  /**
   * Cancel and close the dialog
   */
  protected cancel(): void {
    this.close();
  }

  /**
   * Get initials from tenant businessName
   * Splits by non-alphanumeric characters to avoid initials like "T-"
   * @param tenant - Tenant to get initials from
   * @returns Two-letter initials
   */
  protected getTenantInitials(tenant: Tenant): string {
    if (!tenant.businessName) {
      return '?';
    }
    // Split by non-alphanumeric characters (spaces, dashes, @, etc.)
    const words = tenant.businessName.split(/[^a-zA-Z0-9]+/).filter((w) => w.length > 0);
    if (words.length >= 2) {
      return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
    }
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return '?';
  }
}
